import React, { useState, useEffect } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useTimezone } from "../../lib/context/TimezoneProvider";
import { formatDate, formatDuration } from "../../lib/utils/utility";
import { SEVERITY_CLASSES } from "../../lib/utils/constants";
import { fetchStandingAlarms } from "./hooks/api";

export default function StandingAlarms() {
  const { serialNumber, siteId } = useGlobalFilters();
  const { effectiveTimezone } = useTimezone();
  const [alarms, setAlarms] = useState([]);
  const [alarmCount, setAlarmCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serialNumber || !siteId) {
      setAlarms([]);
      setAlarmCount(0);
      return;
    }

    let cancelled = false;

    async function loadAlarms() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchStandingAlarms(serialNumber, siteId);
        if (cancelled) return;
        setAlarms(res?.alarms || []);
        setAlarmCount(res?.alarmCount || 0);
      } catch {
        if (cancelled) return;
        setError("Failed to load standing alarms");
        setAlarms([]);
        setAlarmCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAlarms();

    return () => {
      cancelled = true;
    };
  }, [serialNumber, siteId]);

  if (loading) {
    return (
      <div className="standing-alarms standing-alarms--loading">
        <div className="standing-alarms__header">
          <span className="standing-alarms__spinner" />
          <span className="standing-alarms__title">Loading standing alarms...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="standing-alarms standing-alarms--error">
        <div className="standing-alarms__header">
          <AlertTriangle size={16} />
          <span className="standing-alarms__title">{error}</span>
        </div>
      </div>
    );
  }

  if (alarmCount === 0) {
    return (
      <div className="standing-alarms standing-alarms--clear">
        <div className="standing-alarms__header">
          <CheckCircle2 size={16} />
          <span className="standing-alarms__title">No standing alarms</span>
        </div>
      </div>
    );
  }

  return (
    <div className="standing-alarms standing-alarms--active">
      <button
        className="standing-alarms__header standing-alarms__header--clickable"
        onClick={() => setExpanded(prev => !prev)}
      >
        <AlertTriangle size={16} />
        <span className="standing-alarms__title">Standing Alarms</span>
        <span className="standing-alarms__badge">{alarmCount}</span>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="standing-alarms__body">
          <table className="standing-alarms__table">
            <thead>
              <tr>
                <th>Alarm Name</th>
                <th>Severity</th>
                <th>Details</th>
                <th>First Set</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {alarms.map((alarm, idx) => (
                <tr key={alarm.eventTypeId || idx}>
                  <td>{alarm.eventName || "-"}</td>
                  <td>
                    {alarm.severity ? (
                      <span className={`standing-alarms__severity ${SEVERITY_CLASSES[alarm.severity] || ""}`}>
                        {alarm.severity}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{alarm.details || "-"}</td>
                  <td>{formatDate(alarm.firstSetAt, effectiveTimezone)}</td>
                  <td>{formatDuration(alarm.firstSetAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
